import React, {createContext, useState, ReactNode, useEffect} from "react";
import {handleLogin} from "@features/auth/action";
import {useDispatch} from "react-redux";
import {AppDispatch, RootState} from "@src/store";
import {LoginRequestI, userSettingsI} from "@features/auth/types";
import {useAppSelector} from "@store/hooks";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import i18n from "i18next";

type AuthContextType = {
    userOptions: userSettingsI | null;
    login: (credentials: LoginRequestI) => Promise<void>;
};

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getDefaultRedirectRoute = (lang: string, userOptions: any, t: any) => {
    const available = userOptions && Object.entries(userOptions)
        .filter(([key, value]) => key !== "_id" && key !== "sessionToken" && value === true);

    if (!available || available.length === 0) return "/";

    if (available.length > 1) return `/${lang}/${t("router.choose-category")}`;
    if (userOptions?.BYODAvailable) return `/${lang}/${t("router.nieuwe-prints")}`;
    if (userOptions?.reservePCAvailable) return `/${lang}/${t("router.create-pc-reservation")}`;
    if (userOptions?.reservePlaceAvailable) return `/${lang}/${t("router.create-workplace-reservation")}`;

    return "/";
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [userOptions, setUserOptions] = useState<userSettingsI | null>(null);
    const dispatch = useDispatch<AppDispatch>();
    const {websiteSettings} = useAppSelector((state: RootState) => state.websiteSettings);
    const navigate = useNavigate();
    const {t} = useTranslation();

    const login = async (credentials: LoginRequestI): Promise<void> => {
        await dispatch(handleLogin(credentials)).unwrap();

        const options = {
            BYODAvailable: websiteSettings.BYODAvailable,
            reservePCAvailable: websiteSettings.reservePCAvailable,
            reservePlaceAvailable: websiteSettings.reservePlaceAvailable,
        };

        localStorage.setItem("userSettings", JSON.stringify(options));

        setUserOptions(options);

        const lang = i18n.language;
        const redirectRoute = getDefaultRedirectRoute(lang, options, t);
        navigate(redirectRoute);
    };

    useEffect(() => {
        const savedOptions = localStorage.getItem("userSettings");
        if (savedOptions) {
            setUserOptions(JSON.parse(savedOptions));
        }
    }, []);

    return (
        <AuthContext.Provider value={{ userOptions, login }}>
            {children}
        </AuthContext.Provider>
    );
};